import type { projectDetailUrlTypeEnum } from '@/features/mold/models';
import {
  type ReportStandardFundsForPublicModel,
  reportStandardFundReportForPublicCsvHeader,
} from '@/features/mold/models/standard-fund';
import { isBefore2025 } from '@/features/project/_utils/project';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import {
  BusinessFormLabelModifier,
  OperationFormLabelModifier,
} from '@/features/project/projects/_models/project';
import { calcAdministrativeExpensesRate } from '@/features/project/projects/detail/income-and-expenditure/_utils/fund-income-and-expenditure-table';
import { t } from '@/i18n';
import { createCsv, formatAmountForCsv } from '@/utils';
import type { z } from 'zod';
import { getPublicReportTargetYear } from './private-csv';
import { standardFundReportForPublicValueConverter } from './public-converter';

export const getStandardFundReportForPublicCsvHeader = (
  fiscalYear: number,
): Record<
  z.infer<typeof reportStandardFundReportForPublicCsvHeader>,
  string
> => {
  const reportTargetYear = getPublicReportTargetYear(fiscalYear);
  const previousReportTargetYear = reportTargetYear - 1;

  return {
    fundSheetNumber: t('report-standard-fund-header-fund-sheet-number'),
    ministryName: t('ministry'),
    additionalJurisdictionalMinistries: t('additional-organization-in-charge'),
    fundBusinessName: t('fund-business-name'),
    projectName: t('fund-name'),
    fundOwner: t('fund-owner'),
    operationForms: t('operation-form'),
    businessForms: t('business-form'),
    beginningOfTheFundBalance: t(
      'report-standard-fund-header-public-beginning-of-the-fund-balance',
      { year: previousReportTargetYear },
    ),
    totalIncome: t('report-standard-fund-header-public-total-income', {
      year: reportTargetYear,
    }),
    totalExpenses: t('report-standard-fund-header-public-total-expenses', {
      year: reportTargetYear,
    }),
    administrativeExpense: t(
      'report-standard-fund-public-header-administrative-expense',
    ),
    administrativeExpenseRate: t(
      'report-standard-fund-public-header-administrative-expense-rate',
    ),
    amountReturnedToTheNationalTreasury: t(
      'report-standard-fund-public-header-amount-returned-to-the-national-treasury',
      { year: reportTargetYear },
    ),
    amountReturnedToTheOrganization: isBefore2025({ fiscalYear })
      ? t(
          'report-standard-fund-public-header-amount-returned-to-the-organization-before-2025',
          { year: reportTargetYear },
        )
      : t(
          'report-standard-fund-public-header-amount-returned-to-the-organization',
          { year: reportTargetYear },
        ),
    endingOfTheFundBalance: t(
      'report-standard-fund-public-header-ending-of-the-fund-balance',
      {
        year: reportTargetYear,
      },
    ),
    projectDetailPageUrl: t('project-detail-page-url'),
  };
};

export const convertStandardFundReportForPublicToCsv = ({
  headers,
  reports,
  isDebugMode,
  projectDetailUrlType,
}: {
  headers: Record<
    z.infer<typeof reportStandardFundReportForPublicCsvHeader>,
    string
  >;
  reports: z.infer<typeof ReportStandardFundsForPublicModel>;
  isDebugMode: boolean;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
}) => {
  const csvFormattedReports = reports.data.map((report) => {
    const values = standardFundReportForPublicValueConverter({
      report,
      type: 'csv',
      isDebugMode,
    });
    const projectDetailPageUrl = getProjectDetailPageUrl({
      isPS: projectDetailUrlType === 'public',
      projectId: report.id,
    });
    return {
      fundSheetNumber: `${report.fundSheetNumber.toString()}${
        report.fundSheetBranchNumber ? `-${report.fundSheetBranchNumber}` : ''
      }`,
      ministryName: report.ministryName,
      additionalJurisdictionalMinistries:
        report.additionalJurisdictionalMinistries?.join('、') ?? '',
      fundBusinessName: report.fundBusinessName,
      projectName: report.projectName,
      fundOwner: report.fundOwner,
      operationForms:
        report.operationForms
          ?.map((d) => OperationFormLabelModifier[d])
          .join('、') ?? '',
      businessForms:
        report.businessForms
          ?.map((d) => BusinessFormLabelModifier[d])
          .join('、') ?? '',
      beginningOfTheFundBalance: formatAmountForCsv(
        report.beginningOfTheFundBalance,
      ),
      totalIncome: values.totalIncome,
      totalExpenses: values.totalExpenses,
      administrativeExpense: formatAmountForCsv(report.administrativeExpense),
      // TODO: https://linear.app/wise-vine/issue/PRZ-3238/improve-基金の乖離率の計算ロジックをbeに寄せる
      administrativeExpenseRate: calcAdministrativeExpensesRate({
        administrativeExpense: report.administrativeExpense ?? 0,
        sum: report.totalExpenses,
        hideUnit: true,
      }),
      amountReturnedToTheNationalTreasury: formatAmountForCsv(
        report.amountReturnedToTheNationalTreasury,
      ),
      amountReturnedToTheOrganization: formatAmountForCsv(
        report.amountReturnedToTheOrganization,
      ),
      endingOfTheFundBalance: values.endingOfTheFundBalance,
      projectDetailPageUrl,
    };
  });

  const csvData = createCsv({
    headerKeys: reportStandardFundReportForPublicCsvHeader.options,
    headerConverter: headers,
    body: csvFormattedReports,
  });

  return csvData;
};
